import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { APPROVAL_STATUS, TASK_KIND, type EmployerTodayTaskItem } from '@fdh/shared';
import { EmployerHistoryStatusIcon } from './EmployerHistoryStatusIcon';
import { EmployerStatusRibbon } from './EmployerStatusRibbon';
import { ListNavRow } from '../../ui/ListNavRow';
import { isEmployerTaskInstanceOverdue } from '../../../utils/employerTaskDayGrouping';
import {
  extractEmployerTaskCompletionDisplay,
  shouldShowEmployerTaskCompletionTime,
} from '../../../utils/employerTaskCompletionDisplay';

type EmployerTaskDayRowProps = {
  task: EmployerTodayTaskItem;
  todayYmd: string;
  currentMinutes: number;
  t: (key: string, params?: Record<string, string | number>) => string;
  onOpenDetail: (taskInstanceId: string) => void;
  onEditTemplate: (task: EmployerTodayTaskItem) => void;
  onReview: (taskInstanceId: string) => void;
};

type MenuAnchor = {
  top: number;
  left: number;
};

const MENU_WIDTH_PX = 144;

const MoreVerticalIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={3}>
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="19" r="1" />
    </g>
  </svg>
);

export const EmployerTaskDayRow = ({
  task,
  todayYmd,
  currentMinutes,
  t,
  onOpenDetail,
  onEditTemplate,
  onReview,
}: EmployerTaskDayRowProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchor | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const isAwaitingSubmission =
    task.status === APPROVAL_STATUS.PENDING &&
    (!task.submittedAt || String(task.submittedAt).trim() === '');
  const isPendingReview =
    task.status === APPROVAL_STATUS.PENDING &&
    Boolean(task.submittedAt && String(task.submittedAt).trim() !== '');
  const isOverdue = isEmployerTaskInstanceOverdue(task, todayYmd, currentMinutes);

  const updateMenuAnchor = useCallback(() => {
    const button = menuButtonRef.current;
    if (!button) {
      return;
    }
    const rect = button.getBoundingClientRect();
    setMenuAnchor({
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - MENU_WIDTH_PX),
    });
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuAnchor(null);
      return;
    }
    updateMenuAnchor();
    const handleReposition = () => updateMenuAnchor();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [menuOpen, updateMenuAnchor]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || menuButtonRef.current?.contains(target)) {
        return;
      }
      setMenuOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  const handleRowClick = () => {
    onOpenDetail(task.taskInstanceId);
  };

  const handleToggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen((prev) => !prev);
  };

  const handleMenuKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      setMenuOpen((prev) => !prev);
    }
  };

  const titleForAria =
    task.taskKind === TASK_KIND.INVENTORY
      ? `${task.title} (${t('task.list.kindStockTake')})`
      : task.title;

  const completionDisplay = shouldShowEmployerTaskCompletionTime(task.status, task.submittedAt)
    ? extractEmployerTaskCompletionDisplay(task.submittedAt, task.dueDate)
    : null;
  const hasSubtitleLine = Boolean(task.dueTime || completionDisplay);

  return (
    <li>
      <ListNavRow
        root="div"
        onClick={handleRowClick}
        ariaLabel={titleForAria}
        align={hasSubtitleLine ? 'start' : 'center'}
        comfortable
        showChevron={false}
        ribbon={<EmployerStatusRibbon status={task.status} submittedAt={task.submittedAt} />}
        leading={
          <EmployerHistoryStatusIcon
            status={task.status}
            isAwaitingSubmission={isAwaitingSubmission}
            isOverdue={isOverdue}
          />
        }
        trailing={
          <button
            ref={menuButtonRef}
            type="button"
            onClick={handleToggleMenu}
            onKeyDown={handleMenuKeyDown}
            tabIndex={0}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label={t('task.list.taskRowMenuAria', { title: task.title })}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <MoreVerticalIcon />
          </button>
        }
      >
        <div className="min-w-0 pr-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold leading-tight text-gray-900 dark:text-gray-100">
              {task.title}
            </h3>
            {task.taskKind === TASK_KIND.INVENTORY ? (
              <span className="inline-flex shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
                {t('task.list.kindStockTake')}
              </span>
            ) : null}
          </div>
          {task.dueTime || completionDisplay ? (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {task.dueTime ? (
                <span>{t('task.today.dueAt', { time: task.dueTime })}</span>
              ) : null}
              {task.dueTime && completionDisplay ? (
                <span aria-hidden className="mx-1">
                  ·
                </span>
              ) : null}
              {completionDisplay ? (
                <span>{t('task.history.completionTime', { time: completionDisplay })}</span>
              ) : null}
            </p>
          ) : null}
        </div>
      </ListNavRow>
      {menuOpen && menuAnchor
        ? createPortal(
            <div
              ref={menuRef}
              className="fixed z-[120] overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
              style={{ top: menuAnchor.top, left: menuAnchor.left, width: MENU_WIDTH_PX }}
              role="menu"
            >
              <button
                type="button"
                role="menuitem"
                tabIndex={0}
                className="flex w-full items-center gap-2.5 border-b border-gray-200/40 px-4 py-3 text-left text-[13px] font-medium text-gray-900 hover:bg-gray-50 focus:outline-none dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  onEditTemplate(task);
                }}
              >
                {t('task.list.menuEditTask')}
              </button>
              <button
                type="button"
                role="menuitem"
                tabIndex={0}
                className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-[13px] font-medium text-gray-900 hover:bg-gray-50 focus:outline-none dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  if (isPendingReview) {
                    onReview(task.taskInstanceId);
                    return;
                  }
                  onOpenDetail(task.taskInstanceId);
                }}
              >
                {isPendingReview ? t('task.list.menuReviewTask') : t('task.list.menuViewTask')}
              </button>
            </div>,
            document.body,
          )
        : null}
    </li>
  );
};
